import React from 'react';
import { Asset, InfoFilledIcon } from '@clearcover/atlas';

import {
  FeeLineProps,
  FeeModalProps,
  useFeeModal,
} from 'QuoteApp/screens/Purchase/FeeModal';
import { PaymentPlanType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { isPayInFullPlan } from 'QuoteApp/utils/paymentPlan';
import { formatPrice } from 'Shared/utils';

import { SectionWithTitle } from './base/Section';
import { InfoRow } from './base/InfoRow';
import { PricingSectionProps } from './types';
import styles from './styles.module.css';

export const PricingSection: React.FC<PricingSectionProps> = ({
  paymentPlan,
  predictableProtectionFeeApplicable,
}) => {
  const { showFeeModal, FeeModalPortal } = useFeeModal();

  const getFeeModalInfo = (
    paymentPlan: PaymentPlanType
  ): Omit<FeeModalProps, 'open' | 'onClose'> => {
    const paymentSchedule = paymentPlan.paymentSchedule;

    /* Down payment values */
    const downPaymentInfo = paymentSchedule?.find(
      (entry) => entry.type === 'DOWNPAYMENT'
    );
    const downPaymentPredictableProtectionFee = downPaymentInfo?.items.find(
      (item) => item.name === 'predictableProtection'
    );
    const downPaymentPolicyFee = downPaymentInfo?.items.find(
      (item) => item.name === 'policyFee'
    );

    /* Installment values */
    const installmentInfo = paymentSchedule?.find(
      (entry) => entry.type === 'INSTALLMENT'
    );
    const installmentPredictableProtectionFee = installmentInfo?.items.find(
      (item) => item.name === 'predictableProtection'
    );
    const installmentPolicyFee = installmentInfo?.items.find(
      (item) => item.name === 'policyFee'
    );

    const infoCopy = isPayInFullPlan(paymentPlan)
      ? 'Your payment includes the following fees:'
      : 'Your installment payments include the following fees:';

    const predictableProtectionFeeAmount = isPayInFullPlan(paymentPlan)
      ? downPaymentPredictableProtectionFee?.amount
      : installmentPredictableProtectionFee?.amount;

    const policyFeeAmount = isPayInFullPlan(paymentPlan)
      ? downPaymentPolicyFee?.amount
      : installmentPolicyFee?.amount;

    return {
      title: 'Total Cost',
      infoCopy,
      feeLines: [
        !!predictableProtectionFeeAmount
          ? {
              feeName: 'Predictable protection fee',
              feeAmount: formatPrice(predictableProtectionFeeAmount),
            }
          : null,
        !!policyFeeAmount
          ? { feeName: 'Policy fee*', feeAmount: formatPrice(policyFeeAmount) }
          : null,
      ].filter((line): line is FeeLineProps => line !== null),
      footNote:
        !isPayInFullPlan(paymentPlan) && downPaymentPolicyFee?.amount
          ? `*${formatPrice(
              downPaymentPolicyFee.amount
            )} policy fee also included in down payment`
          : '',
    };
  };

  const renderFeeModalTrigger = () => (
    <span data-testid="fee-modal-trigger" className={styles.feeModalTrigger}>
      <button onClick={() => showFeeModal(getFeeModalInfo(paymentPlan))}>
        <Asset
          icon={InfoFilledIcon}
          height={18}
          width={18}
          description="Fee Breakdown"
          className={styles.feeModalTooltip}
        />
      </button>
    </span>
  );

  return (
    <>
      <SectionWithTitle title="Total cost for 6-month policy">
        {paymentPlan.totalAmountBreakdown?.map(
          ({ name, formattedAmount }, index) => (
            <InfoRow key={index} label={name} value={formattedAmount} />
          )
        )}
        <InfoRow
          label="Total cost"
          value={paymentPlan.totalAmount}
          labelAddon={
            predictableProtectionFeeApplicable ? renderFeeModalTrigger() : null
          }
          boldLabel
          boldValue
        />
        {!predictableProtectionFeeApplicable && (
          <div className={styles.feeDisclaimer}>
            <span>
              Additional fees may be incurred for certain policy changes and
              missed or late payments.
            </span>
          </div>
        )}
      </SectionWithTitle>
      {predictableProtectionFeeApplicable && FeeModalPortal}
    </>
  );
};
